import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { ProductCard, type Product } from './ProductCard';
import { DEMO_PRODUCTS } from '@/data/demoProducts';
import { getOutfitSuggestions, garmentType, productColour, type TextSource } from '@/lib/catalog';

interface Props {
  product: TextSource;
  className?: string;
}

interface AiPick {
  reason: string;
  product: Product;
}
interface LookResponse {
  engine: 'ai' | 'rules';
  picks: AiPick[];
}

/**
 * "Complete the look" - a Souqli Membership perk.
 *
 * Souqli's AI-powered stylist (`POST /api/ai/complete-the-look`) picks
 * live listings that pair with the item being viewed - trousers + trainers
 * for a shirt, shoes + bag for a dress, a strap for a watch - each with a
 * one-line stylist reason. Membership, caching, a per-listing cooldown and a
 * monthly allowance are all enforced server-side; this component only ever
 * fires the request when the member explicitly asks for it, never on page
 * load/refresh, so a visitor who never clicks costs nothing.
 *
 * Local rule-based picks (over the demo catalogue) are kept only as a
 * last-resort fallback for a member whose AI response came back too sparse -
 * not as a free-tier substitute for the feature itself.
 */
export function CompleteTheLook({ product, className = '' }: Props) {
  const [requested, setRequested] = useState(false);
  // A signed-out visitor can't have a membership, so we never fire the
  // request for them - we show the membership prompt the moment they ask.
  const user = useAuthStore((s) => s.user);

  const look = useMutation({
    mutationFn: async () => (await api.post<LookResponse>('/ai/complete-the-look', {
      base: {
        id: product.id,
        title: product.title ?? '',
        brand: product.brand,
        category: product.category,
        colour: productColour(product),
      },
      limit: 4,
    })).data,
  });

  const localPicks = useMemo(
    () => getOutfitSuggestions(product, DEMO_PRODUCTS, 4),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product.id],
  );

  if (!product.title) return null;

  const colour = productColour(product);
  const type = garmentType(product);
  const lead = colour ? `${colour} ${type}` : `this ${type}`;
  const errorCode = (look.error as any)?.response?.data?.code as string | undefined;

  const aiBacked = (look.data?.picks?.length ?? 0) >= 2;
  const picks: { key: string; card: Product; reason: string }[] = aiBacked
    ? look.data!.picks.map((p) => ({ key: p.product.id, card: p.product, reason: p.reason }))
    : localPicks.map(({ item, reason }) => ({ key: item.id, card: item, reason }));
  const aiEngine = aiBacked && look.data!.engine === 'ai';
  const showLocalFallback = requested && look.isSuccess && !aiBacked && picks.length > 0;

  return (
    <section className={`mt-16 ${className}`}>
      {/* ── Editorial header: ornamental rule + serif title, centred ── */}
      <div className="flex items-center gap-4 mb-7">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-warmline to-warmline" />
        <span className="stamp text-gold-dark text-[10px] tracking-[0.34em] whitespace-nowrap">STYLE IT WITH</span>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent via-warmline to-warmline" />
      </div>

      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold-soft/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold-dark">
          <Sparkle /> Souqli Membership perk
        </span>

        <h2 className="display text-3xl md:text-[2.6rem] leading-[1.05] text-ink mt-4">Complete the look.</h2>

        <p className="text-mute text-[13.5px] leading-relaxed mt-3 max-w-lg mx-auto">
          {aiEngine
            ? <>Pieces our AI stylist hand-picked to pair with <span className="text-ink font-medium">{lead}</span>.</>
            : showLocalFallback
              ? <>Pieces our stylists would pair with <span className="text-ink font-medium">{lead}</span>.</>
              : <>Let Souqli&rsquo;s AI stylist curate pieces that pair beautifully with <span className="text-ink font-medium">{lead}</span>.</>}
        </p>

        {aiEngine && (
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 text-[10px] font-medium text-gold-light" title="Matched by Souqli's AI stylist">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /> Styled by Souqli AI
          </span>
        )}

        {!requested && (
          <div className="mt-6">
            <button
              onClick={() => { setRequested(true); if (user) look.mutate(); }}
              className="btn btn-gold !text-ink inline-flex items-center gap-2 px-6 shadow-glow"
            >
              <Sparkle /> Style this piece
            </button>
          </div>
        )}
      </div>

      <div className="mt-10">
        {/* Signed-out: this is a members-only perk, and they need an account
            before they can take out Membership, so we point them to both. */}
        {requested && !user && (
          <div className="card-flat p-6 max-w-md mx-auto text-center">
            <div className="mx-auto mb-3 grid place-items-center w-11 h-11 rounded-full bg-gold-soft text-gold-dark"><Sparkle /></div>
            <p className="text-sm text-ink">Complete the Look is a Souqli Membership perk. Log in and unlock Membership to style this piece.</p>
            <div className="flex items-center justify-center gap-2.5 mt-4">
              <Link to="/login" className="btn btn-sm btn-outline">Log in</Link>
              <Link to="/membership" className="btn btn-sm btn-gold !text-ink">Unlock with Membership</Link>
            </div>
          </div>
        )}

        {requested && user && look.isPending && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-8">
            {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[3/4] skeleton rounded-luxe" />)}
          </div>
        )}

        {requested && errorCode === 'MEMBERSHIP_REQUIRED' && (
          <div className="card-flat p-6 max-w-md mx-auto text-center">
            <div className="mx-auto mb-3 grid place-items-center w-11 h-11 rounded-full bg-gold-soft text-gold-dark"><Sparkle /></div>
            <p className="text-sm text-ink">Complete the Look is a Souqli Membership perk.</p>
            <Link to="/membership" className="btn btn-sm btn-gold !text-ink mt-4 inline-block">Unlock with Membership</Link>
          </div>
        )}

        {requested && errorCode === 'COMPLETE_LOOK_QUOTA_EXCEEDED' && (
          <div className="card-flat p-6 max-w-md mx-auto text-center">
            <p className="text-sm text-ink">You&rsquo;ve reached your monthly Complete the Look limit. It refreshes next month.</p>
          </div>
        )}

        {requested && look.isSuccess && picks.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-8">
            {picks.map(({ key, card, reason }) => (
              <div key={key} className="group/pick">
                <ProductCard product={card} />
                <p className="mt-2.5 text-[11px] leading-snug text-mute italic border-l-2 border-gold/40 pl-2.5">{reason}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Small four-point sparkle used across the Complete-the-Look header. */
function Sparkle() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
      <path d="M12 0c.5 5.5 3 8 8.5 8.5C15 9 12.5 11.5 12 17c-.5-5.5-3-8-8.5-8.5C9 8 11.5 5.5 12 0Z" opacity=".95" />
      <path d="M19 14c.2 2.4 1.3 3.5 3.7 3.7-2.4.2-3.5 1.3-3.7 3.7-.2-2.4-1.3-3.5-3.7-3.7 2.4-.2 3.5-1.3 3.7-3.7Z" opacity=".7" />
    </svg>
  );
}
